import { SubmitHelpers } from '@area2k/use-form';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useApolloClient } from '@apollo/client';
import { useState } from 'react';
import { useParams } from 'react-router-dom';

import { SelectItem } from '..';
import { JobStatus, getJobStatusByJob } from '../../Job/util';

import Button from '@/components/Button';
import Card from '@/components/Card';
import ConfirmationModal from '@/components/ConfirmationModal';
import IconicButton from '@/components/IconicButton';
import LtaWorkersList from '@/components/LtaWorkersList';
import Modal from '@/components/Modal';
import Stack from '@/components/Stack';
import { Body, Small } from '@/components/Typography';
import WorkerAutocompleteField, {
  WorkerItem,
} from '@/components/WorkerAutocompleteField';
import Form from '@/form';
import useAuth from '@/hooks/useAuth';
import useOvertimeGate from '@/hooks/useOvertimeGate';
import { JobActionError, JobsAPI } from '@/services/jobsAPI';
import {
  GetOrderQuery,
  JobTypeEnum,
  RelationshipKindEnum,
  Scalars,
  TaxTypeEnum,
} from '@/types/graphql';
import { ErrorMap, handleApiFormError } from '@/util/error';
import { workerName } from '@/util/worker';
import { trackWorkerHired, trackWorkerInvited } from '@/util/vexo';
import type { OvertimeOutcome } from '@/hooks/useOvertimeGate';

export const JOB_HIRE_AND_OFFER_ERROR_MAP: ErrorMap = {
  // GRAVY-10421: unhandled backend 500s reach the client as SERVER_ERROR.
  SERVER_ERROR: () => ({
    title: 'Server error',
    message:
      'Something went wrong on our end. Please try again or contact support.',
    status: 'danger',
  }),
  ALREADY_HIRED: () => ({
    title: 'GravyWorker already hired',
    message: 'This GravyWorker has already been hired for this shift.',
  }),
  ALREADY_OFFERED: () => ({
    title: 'Offer already sent',
    message: 'This GravyWorker has already been offered this shift.',
  }),
  INVALID_JURISDICTION: () => ({
    title: "Gravyworker's onboarding isn't complete",
    message:
      '{0} needs to complete prerequisites to be hired. Please invite them instead.',
  }),
  OVERLAPPING_HOURS: (gqlError) => ({
    title: 'Overlapping hours',
    message: gqlError.message,
  }),
  WORKER_ONBOARDING_NOT_COMPLETED: () => ({
    title: "Gravyworker's onboarding isn't complete",
    message:
      '{0} needs to complete prerequisites to be hired. Please invite them instead.',
  }),
  WORKER_REQUIRED_SKILL_MISSING: () => ({
    title: `This GravyWorker doesn't have the requested skill`,
    message: 'Verify that the GravyWorker is able to work in this position.',
  }),
  QUALITY_GATE_BLOCKED: () => ({
    title: 'Dependability Gate Restriction',
    message: '{0} does not meet the reliability requirements for this shift.',
  }),
  JOB_UNAVAILABLE: () => ({
    title: 'Error',
    message:
      'Another GravyWorker has already been hired for this shift. You can no longer hire it to GravyWorkers.',
    status: 'danger',
  }),
  WORKER_ALREADY_DROPPED_JOB: (gqlError) => ({
    title: 'Error',
    message: gqlError.message,
  }),
  JOB_FILLED: () => ({
    title: 'Shift already filled',
    message:
      'Another GravyWorker has already been hired for this shift. You can no longer offer it to GravyWorkers.',
    status: 'danger',
  }),
  JOB_IN_PAST: () => ({
    title: 'Error',
    message: 'Cannot invite workers for the past Shifts.',
  }),
  WORKER_OFFER_REJECTED: (gqlError) => ({
    title: 'Error',
    message: gqlError.message,
    status: 'danger',
  }),
  // GRAVY-10437: the worker's user record is missing, so there is nobody to
  // notify and nothing to accept the invite. graphy refuses before any write.
  WORKER_USER_MISSING: () => ({
    title: 'Worker cannot be staffed',
    message:
      '{0} has no user account, so they cannot be invited or hired. Please contact support.',
    status: 'danger',
  }),
  WORKER_IS_BLOCKED: () => ({
    title: 'Error',
    message: 'GravyWorker is blocked for this client account',
    status: 'danger',
  }),
  JOB_PAST_TWO_WEEKS: () => ({
    title: 'Shift is too far in the past',
    message:
      'Workers cannot be hired for shifts that completed more than two weeks ago.',
  }),
  WORKER_INACTIVE_OR_SUSPENDED: () => ({
    title: 'GravyWorker unavailable',
    message: '{0} is inactive, suspended, or banned and cannot be hired.',
  }),
  SSN_UNVERIFIED: () => ({
    title: 'SSN verification required',
    message: '{0} has not completed SSN verification.',
    status: 'danger',
  }),
  BACKGROUND_CHECK_REQUIRED: () => ({
    title: 'Background check required',
    message: '{0} is not eligible for shifts that require a background check.',
    status: 'danger',
  }),
};

type FormValues = {
  worker: WorkerItem | null;
};

type Props = {
  hideModal: () => Scalars['Void'];
  onConfirm: () => Scalars['Void'];
  deleteItem: (id: Scalars['String']) => Scalars['Void'];
  selectedJobs: SelectItem[];
  customer: GetOrderQuery['order']['account']['customer'];
  context: 'hire' | 'invite';
  hasJobsForInvite?: boolean;
  address: any;
};

const InviteAndHireModal = ({
  hideModal,
  onConfirm,
  deleteItem,
  selectedJobs,
  context,
  customer,
  hasJobsForInvite = true,
  address,
}: Props) => {
  const { id: customerId, jobTaxType } = customer;
  // orderId from the Agency/Order route — same `useParams` source as
  // the parent screen (`/agency/orders/:orderId`). The modal is rendered
  // inside that route so router context is still available.
  const { orderId } = useParams<{ orderId: string }>();
  const [errorjobs, setErrorJobs] = useState<any>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [rehireWorkerId, setRehireWorkerId] = useState('');
  const { currentAdminIsCustomerAdmin } = useAuth();
  const { check: checkOvertime, modal: overtimeModal } = useOvertimeGate();
  const apolloClient = useApolloClient();
  const [isLoading, setIsLoading] = useState(false);

  // Mirrors the refetchQueries: ['GetOrder'] that the old GraphQL mutation
  // hooks ran automatically — refreshes the parent order page after a hire
  // or offer so the worker shows up without a manual refresh.
  const refetchOrder = async () => {
    try {
      await apolloClient.refetchQueries({ include: ['GetOrder'] });
    } catch {
      // Best-effort; cache stays stale until next navigation.
    }
  };

  const skillsSelected = [
    ...new Set(selectedJobs.map((item) => item.skill.id)),
  ] as [string];
  const jurisdictionSelected = [
    ...new Set(selectedJobs.map((item) => item.jurisdiction)),
  ] as [string];

  const initialValues: FormValues = {
    worker: null,
  };
  const isLtaJob =
    selectedJobs?.length > 0
      ? selectedJobs[0].jobType === JobTypeEnum.LTA
      : false;

  // Submit a single (job, worker) hire/invite. Returns:
  //  { ok: true } on success
  //  { rehirable: true } when the action failed with WORKER_ALREADY_DROPPED_JOB / ALREADY_OFFERED
  //  { error: true } when any other error fired (form error is set inline)
  const submitOne = async (
    job: SelectItem,
    workerId: string,
    worker: WorkerItem,
    setFormError: SubmitHelpers['setFormError'],
    force: boolean,
    otOutcome: OvertimeOutcome | null,
  ): Promise<{ ok?: boolean; rehirable?: boolean; error?: boolean }> => {
    try {
      if (context === 'invite') {
        const jobStatus = getJobStatusByJob(job);
        if (jobStatus !== JobStatus.UPCOMING) return { ok: true };
        await JobsAPI.createOffer(job.id, { workerId, forceInvite: force });
        // Vexo `worker_invited`. Force-invites (rehire) skip the event,
        // matching the OT-gate force-skip convention.
        if (!force) {
          trackWorkerInvited({
            job_id: job.id,
            worker_id: workerId,
            worker_current_hours: otOutcome?.worker_current_hours ?? null,
            overtime_triggered: otOutcome?.triggered ?? false,
            overtime_hours: otOutcome?.overtime_hours ?? null,
          });
        }
      } else {
        await JobsAPI.hireWorker(job.id, { workerId, forceHire: force });
        if (!force) {
          trackWorkerHired({
            job_id: job.id,
            worker_id: workerId,
            worker_current_hours: otOutcome?.worker_current_hours ?? null,
            overtime_triggered: otOutcome?.triggered ?? false,
            overtime_hours: otOutcome?.overtime_hours ?? null,
          });
        }
      }
      return { ok: true };
    } catch (err: any) {
      if (
        err instanceof JobActionError &&
        !currentAdminIsCustomerAdmin &&
        (err.code === 'WORKER_ALREADY_DROPPED_JOB' ||
          err.code === 'ALREADY_OFFERED')
      ) {
        return { rehirable: true };
      }
      if (err instanceof JobActionError) {
        handleApiFormError(
          err,
          { setFormError, errorMap: JOB_HIRE_AND_OFFER_ERROR_MAP },
          workerName(worker),
        );
      }
      return { error: true };
    }
  };

  const handleSubmit = async (
    values: FormValues,
    { setFormError }: SubmitHelpers,
  ): Promise<void> => {
    const worker = values.worker!;
    const workerId = worker.id;
    const rehireJobs: SelectItem[] = [];
    let hasError = false;

    setIsLoading(true);
    try {
      for (let i = 0; i < selectedJobs.length; i++) {
        const job = selectedJobs[i];
        // Per-job OT check: the gate either proceeds (with OT context) or
        // the admin cancels for this job (resolve null). Cancel skips this
        // job and the loop continues with the next selected job.
        const otOutcome = await new Promise<OvertimeOutcome | null>(
          (resolve) => {
            checkOvertime(
              job.id,
              worker,
              context === 'hire' ? 'hire' : 'invite',
              (outcome) => resolve(outcome),
              { onCancel: () => resolve(null) },
            );
          },
        );
        if (!otOutcome) continue;

        const result = await submitOne(
          job,
          workerId,
          worker,
          setFormError,
          false,
          otOutcome,
        );
        if (result.rehirable) {
          setRehireWorkerId(workerId);
          rehireJobs.push(job);
        } else if (result.error) {
          hasError = true;
        }
      }

      if (rehireJobs.length > 0) {
        setErrorJobs(rehireJobs);
        setShowConfirmModal(true);
      } else if (!hasError) {
        await refetchOrder();
        onConfirm();
        hideModal();
      } else {
        // Even if something failed, refresh so any partial successes show up.
        await refetchOrder();
      }
    } finally {
      setIsLoading(false);
    }
  };

  const jobslabels = errorjobs.map((e) => e.label)?.join(', ');

  const handleRehire = async () => {
    setIsLoading(true);
    try {
      for (let i = 0; i < errorjobs.length; i++) {
        const job = errorjobs[i];
        try {
          if (context === 'invite') {
            await JobsAPI.createOffer(job.id, {
              workerId: rehireWorkerId,
              forceInvite: true,
            });
          } else {
            await JobsAPI.hireWorker(job.id, {
              workerId: rehireWorkerId,
              forceHire: true,
            });
          }
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e);
        }
      }
      await refetchOrder();
      onConfirm();
      hideModal();
    } finally {
      setIsLoading(false);
    }
  };

  const showError =
    selectedJobs.length < 1 || (context === 'invite' && !hasJobsForInvite);

  return (
    <>
      <Modal
        disableClickout
        size="sm"
        title={
          context === 'hire'
            ? 'Hire Gravyworker for this shift'
            : 'Offer shift to GravyWorker'
        }
        onRequestClose={hideModal}
      >
        <Card.Section>
          {showError ? (
            <>
              <Body>
                {context === 'invite' && !hasJobsForInvite
                  ? `Please select at least one upcoming shift to ${context}.`
                  : `Please select at least one shift to ${context}.`}
              </Body>
              <Stack justify="end">
                <Button a11yLabel="Ok" label="Ok" onClick={hideModal} />
              </Stack>
            </>
          ) : (
            <Stack vertical gap={16}>
              <Stack vertical>
                <Body>Shifts Selected ({selectedJobs.length})</Body>
                <Stack vertical>
                  {selectedJobs.map((job, index) => (
                    <Stack key={index} gap={0}>
                      <IconicButton
                        a11yLabel="delete item"
                        appearance="clear"
                        icon={faTimes}
                        iconTheme={'lighter'}
                        size="xs"
                        type="button"
                        onClick={() => deleteItem(job.id)}
                      />
                      <Small key={job.id}>{job.label}</Small>
                    </Stack>
                  ))}
                </Stack>
              </Stack>

              <Stack>
                <Form
                  initialValues={initialValues}
                  style={{ width: '100%' }}
                  onSubmit={handleSubmit}
                >
                  {context === 'hire' && isLtaJob ? (
                    <LtaWorkersList
                      address={address}
                      customerId={customerId}
                      skillIds={skillsSelected}
                    />
                  ) : (
                    <WorkerAutocompleteField
                      context="multipleJobSelection"
                      fieldId="worker"
                      jurisdiction={jurisdictionSelected}
                      label="Select GravyWorker"
                      relationShip={{
                        customerId,
                        kind: RelationshipKindEnum.BLOCKED,
                      }}
                      skillsId={skillsSelected}
                      {...(jobTaxType === 'w2' && {
                        taxType: TaxTypeEnum.TAX_W2,
                      })}
                    />
                  )}

                  <Stack justify="end">
                    <Button
                      a11yLabel="Submit form"
                      isLoading={isLoading}
                      label={context === 'hire' ? 'Hire' : 'Invite'}
                      type="submit"
                    />
                  </Stack>
                </Form>
              </Stack>
            </Stack>
          )}
        </Card.Section>
      </Modal>
      {showConfirmModal && (
        <ConfirmationModal
          acceptAction={handleRehire}
          acceptButtonLabel={context === 'hire' ? 'Hire worker' : 'Send Invite'}
          bodyContentText={
            context === 'hire'
              ? `This worker has previously dropped the shifts on : ${jobslabels}. Do you want to proceed?`
              : `  'This worker has previously declined an invitation for the shifts on : ${jobslabels}. Do you want to proceed?'`
          }
          denyAction={() => {}}
          denyButtonLabel="Cancel"
          hideModal={() => setShowConfirmModal(false)}
          title={`Confirmation`}
        />
      )}
      {overtimeModal}
    </>
  );
};

export default InviteAndHireModal;
